import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReportsTab from 'src/pages/reports/tabs/reports.tab';
import { ReportComponentType } from './customize-view/report-view-wrapper';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import { ReportMenuBarSelection } from 'src/pages/client/reports/components/reports-menu-bar';
import { Button } from 'src/components/ui/button';
import { Check, Save, X } from 'lucide-react';
import { DEFAULT_VIEW_SETTINGS } from '../../types/client-view-settings.types';

// Context to provide wrapper functionality to ReportsTab
export interface ReportViewCustomizeContextType {
  componentStates: Record<ReportComponentType, boolean>;
  componentFields: Record<ReportComponentType, string[]>;
  componentDefaults: Record<ReportComponentType, string[]>;
  handleStateChange: (componentType: ReportComponentType, enabled: boolean) => void;
  handleFieldSelectionChange: (componentType: ReportComponentType, selectedFields: string[]) => void;
  handleDefaultSelectionChange: (componentType: ReportComponentType, selectedDefaultTypes: string[]) => void;
}

export const ReportViewCustomizeContext = React.createContext<ReportViewCustomizeContextType | null>(null);

export const useReportViewCustomize = () => {
  const context = React.useContext(ReportViewCustomizeContext);
  if (!context) {
    return null; // Not in customize mode
  }
  return context;
};

const ReportViewCustomize: React.FC = () => {
  const navigate = useNavigate();
  const { activeClient, activeClientId } = useActiveClient();
  const [isDefault, setIsDefault] = useState<boolean>(false);
  // Update URL params with previewClientId before components render
  useEffect(() => {
    if (activeClientId) {
      const currentParams = new URLSearchParams(location.search);
      const currentPreviewClientId = currentParams.get('previewClientId');

      // Only update if the previewClientId is different from what's in the URL
      if (currentPreviewClientId !== activeClientId) {
        currentParams.set('previewClientId', activeClientId);
        navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });
      }
    }
  }, [activeClientId, navigate]);

  const [selection, setSelection] = useState<ReportMenuBarSelection>({
    source: '',
    accounts: [],
    campaigns: [],
    lineItems: [],
    dateFilter: '',
    startDate: '',
    endDate: '',
  } as ReportMenuBarSelection);

  const [componentStates, setComponentStates] = useState<Record<ReportComponentType, boolean>>({
    'metric-universal-display': true,
    'zipcode-heatmap': true,
    'zipcode-rank': true,
    'time-of-day': true,
    'domain-rank': true,
    'app-rank': true,
    'video-completion-rate': true,
    'audio-completion-rate': true,
    'breakdown-table': true,
  });

  const [componentFields, setComponentFields] = useState<Record<ReportComponentType, string[]>>({
    'metric-universal-display': [],
    'zipcode-heatmap': [],
    'zipcode-rank': [],
    'time-of-day': [],
    'domain-rank': [],
    'app-rank': [],
    'video-completion-rate': [],
    'audio-completion-rate': [],
    'breakdown-table': [],
  });

  const [componentDefaults, setComponentDefaults] = useState<Record<ReportComponentType, string[]>>({
    'metric-universal-display': [],
    'zipcode-heatmap': [],
    'zipcode-rank': [],
    'time-of-day': [],
    'domain-rank': [],
    'app-rank': [],
    'video-completion-rate': [],
    'audio-completion-rate': [],
    'breakdown-table': [],
  });

  const handleStateChange = useCallback((componentType: ReportComponentType, enabled: boolean) => {
    setComponentStates(prev => ({
      ...prev,
      [componentType]: enabled,
    }));
    console.log('componentStates', componentType, enabled);
  }, []);

  const handleFieldSelectionChange = useCallback((componentType: ReportComponentType, selectedFields: string[]) => {
    setComponentFields(prev => ({
      ...prev,
      [componentType]: selectedFields,
    }));
    console.log('componentFields', componentType, selectedFields);
  }, []);

  const handleSelectionChange = useCallback((selection: ReportMenuBarSelection) => {
    setSelection(selection);
    console.log('selection', selection);
  }, []);

  const handleDefaultSelectionChange = useCallback((componentType: ReportComponentType, selectedDefaultTypes: string[]) => {
    setComponentDefaults(prev => ({
      ...prev,
      [componentType]: selectedDefaultTypes,
    }));
    console.log('componentDefaults', componentType, selectedDefaultTypes);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    componentStates,
    componentFields,
    componentDefaults,
    handleStateChange,
    handleFieldSelectionChange,
    handleDefaultSelectionChange,
  }), [componentStates, componentFields, componentDefaults, handleStateChange, handleFieldSelectionChange, handleDefaultSelectionChange]);

  const handleSave = () => {
    const toCreate = DEFAULT_VIEW_SETTINGS;
    toCreate.accounts = selection.accounts.map(account => account.id.toString());
    toCreate.metricUniversalDisplay.enabled = componentStates['metric-universal-display'];
    componentFields['metric-universal-display'].forEach((field, index) => {
      if (index < 4) {
        // @ts-expect-error - dynamic valid key
        toCreate.metricUniversalDisplay[`button${index + 1}`].enabled = true;
        // @ts-expect-error - dynamic valid key
        toCreate.metricUniversalDisplay[`button${index + 1}`].default = field as string;
      }
    });
    // console.log('toCreate', toCreate);
  };

  // Provide context to ReportsTab so components can be wrapped dynamically
  return (
    <ReportViewCustomizeContext.Provider value={contextValue}>
      <div className='relative'>
        {/* {selection.accounts.length === 0 && (
          <div className='absolute top-0 left-[350px] z-[9999] flex items-center justify-center animate-bounce'>


            <h1 className='text-sm text-white bg-red-501 p-2 rounded-full flex items-center justify-center'>
              <ArrowLeft className='size-5 mr-1' />
              <span className='ml-2'>
                Select an Account to get started.
              </span>
            </h1>
          </div>
        )} */}

        <div className=''>
          <ReportsTab
            showOldVersionButton={false}
            onSelectionChange={handleSelectionChange}
          />
        </div>
        <div className='sticky -bottom-4 border-t border-gray-200 left-0 text-right w-full z-[99999] flex flex-row items-center justify-between gap-2 bg-white py-4'>
          <div className='flex items-center justify-center'>
            <div className='text-sm font-light text-gray-600 mr-2'>
              Active Accounts:
            </div>
            {selection.accounts.length > 0 ? (
              <div className='flex items-center justify-center gap-2 flex-wrap'>
                {selection.accounts.map(account => (
                  <div
                    key={account.id}
                    className='text-xs font-light text-black p-0.5 px-2 border border-gray-400 rounded-full'
                  >
                    {account.name}
                  </div>
                ))}
              </div>
            ) : (
              <h1 className='text-xs font-semibold text-red-501 p-0.5 px-1'>
                No accounts selected
              </h1>
            )}
          </div>
          <div className='flex items-center justify-center gap-2'>
            <Button
              className={`text-black px-3 rounded-full ${isDefault ? 'bg-green-501 text-white' : 'text-gray-500'}`}
              variant='outline'
              size='med'
              onClick={() => setIsDefault(!isDefault)}
            >
              {isDefault ? <Check className='size-4' /> : <X className='size-4' />}
              Default
            </Button>
            <div className='h-4 w-[1px] bg-gray-400 mx-4' />
            <Button
              className='bg-gray-201 text-black px-3'
              variant='ghost'
              size='med'
            >
              <X className='size-4' />
              Cancel
            </Button>
            <Button
              className='bg-blue-500 text-white px-3'
              variant='ghost'
              size='med'
              disabled={selection.accounts.length === 0 && !isDefault}
              onClick={handleSave}
            >
              <Save className='size-4' />
              Save
            </Button>
          </div>
        </div>
      </div>


    </ReportViewCustomizeContext.Provider>
  );
};

export default ReportViewCustomize;
